import { useCallback, useEffect, useRef, useState } from 'react';

import { sleep } from '@karehero/helpers/timers';
import type { LiveTranscript } from '@karehero/models';

import { usableTranscript } from 'utils/voiceTranscript';
import { useFeatureFlag } from './useFeatureFlags';
import { useLiveTranscript } from './useLiveTranscript';

/** Whether recordings are transcribed as they happen. */
export const LIVE_TRANSCRIBE_FLAG = 'liveVoiceTranscribe';
/** Whether the words can be shown at all; they start hidden. */
export const LIVE_CAPTIONS_FLAG = 'liveVoiceCaptionsVisible';

/** How long after a recording stops its last words take to settle. */
export const TRANSCRIPT_SETTLE_MS = 1500;

export interface UseVoiceTranscriptOptions {
  careCircleId?: string;
  /** The microphone while recording; null once it stops. */
  stream: MediaStream | null;
}

export interface UseVoiceTranscriptResult {
  /** The words as they are made, while transcribing is on at all. */
  caption?: LiveTranscript;
  isShown: boolean;
  /** Shows or hides the words; absent while they cannot be shown. */
  toggleShown?: () => void;
  /** A fault with the words shown; one with words not shown stays quiet. */
  error: string | null;
  /** The transcript once its last words settle, or null to read the audio instead. */
  takeTranscript: (durationSeconds: number) => Promise<string | null>;
  /** Where the API keeps the whole recording, or null to upload it instead. */
  recordingId: string | null;
}

/**
 * useVoiceTranscript transcribes a recording as it happens, so a voice note is
 * read from its words as soon as it is sent. The words stay hidden unless the
 * captions flag lets them be shown and they are toggled on.
 */
export const useVoiceTranscript = ({
  careCircleId,
  stream,
}: UseVoiceTranscriptOptions): UseVoiceTranscriptResult => {
  const isOn = !!useFeatureFlag(LIVE_TRANSCRIBE_FLAG);
  const isCaptionsFlagOn = !!useFeatureFlag(LIVE_CAPTIONS_FLAG);
  const canShow = isOn && isCaptionsFlagOn;
  const [isToggledOn, setIsToggledOn] = useState(false);
  const isShown = canShow && isToggledOn;
  const toggleShown = useCallback(() => setIsToggledOn((s) => !s), []);
  const live = useLiveTranscript({ isEnabled: isOn, careCircleId, stream });

  const latest = useRef(live);
  latest.current = live;
  const endedAt = useRef<number | null>(null);
  useEffect(() => {
    endedAt.current = live.status === 'ended' ? Date.now() : null;
  }, [live.status]);

  const takeTranscript = useCallback(
    async (durationSeconds: number) => {
      if (!isOn || endedAt.current === null) return null;
      const asStopped = latest.current;
      const settling = endedAt.current + TRANSCRIPT_SETTLE_MS - Date.now();
      if (settling > 0) await sleep(settling);
      // A recording started meanwhile has words of its own; keep this one's.
      const heard =
        latest.current.status === 'ended' ? latest.current : asStopped;
      return usableTranscript(heard, !!heard.error, durationSeconds);
    },
    [isOn],
  );

  return {
    caption: isOn ? live : undefined,
    isShown,
    toggleShown: canShow ? toggleShown : undefined,
    error: isShown ? live.error : null,
    takeTranscript,
    recordingId: isOn ? live.recordingId : null,
  };
};
